import * as _ from 'lodash'
import util from './util';
import serviceGateway from './serviceGateway';
import store, { DomainEntityType, getCollection, getDomainEntities, getModel, ModelCollection } from './store';
import * as globalActions from './actions';
import enums from './enum';
import jobModel from './models/jobModel';
import jobCostModel from './models/jobCostModel';
import socketClient from './socketClient';
import globalRelay from './globalRelay';
import siteVisitFactory, { ISiteVisit } from './models/siteVisitModel';
import { ISiteVisitJobCost } from './models/siteVisitJobCostModel';

export const socketClientHandlers = {
	['model.sitevisit.added']: message => {
		const visitKey = message.payload.key;
		const model = store().get('model');
		const jobs = model.get('jobs');

		if (jobs.some(j => j.visitKeys.indexOf(visitKey) !== -1)) {
			fetchSiteVisit({ key: visitKey });
		}
	},
	['model.sitevisit.updated']: message => {
		const keyPath = ['model', 'siteVisits', message.payload.key];
		const existing = store().getIn(keyPath);

		if (existing) {
			if (existing.itemVersion === message.payload.patchTargetVersion) {
				existing.applyPatch(message.payload.patch);
				store().setIn(keyPath, existing);
			}
			else if (existing.itemVersion < message.payload.patchTargetVersion) {
				fetchSiteVisit({ key: message.payload.key });
			}
		}
	}
};

// wrap handlers to allow extensions to override
for (const key of _.keys(socketClientHandlers)) {
	socketClient.on(key, message => {
		socketClientHandlers[key](message);
	});
}

export const fetchSiteVisit = params => {
	return serviceGateway.fetchSiteVisit(params)
		.then(result => {
			if (result.siteVisit) {
				store().setIn(['model', 'siteVisits', result.siteVisit.key], siteVisitFactory(result.siteVisit));
			}
		})
		.catch(err => {
			globalActions.pushAlert({ type: 'warning', message: err.message || err, isCloseable: true });
		});
};

export const fetchSiteVisits = params => {
	return serviceGateway.fetchSiteVisits(params)
		.then(result => {
			const collection = getCollection({ collection: ModelCollection.siteVisits });

			collection.withMutations(c => {
				_.forEach(result.siteVisits, m => {
					c.set(m.key, siteVisitFactory(m));
				});
			});
		})
		.catch(err => {
			globalActions.pushAlert({ type: 'warning', message: err.message || err, isCloseable: true });
		});
};

export const deleteSiteVisit = params => {
	const keyPath = ['model', 'siteVisits', params.siteVisit.key];
	const alteredJobs:any[] = [];

	store().deleteIn(keyPath);
	return serviceGateway.deleteSiteVisit(params)
		.then(() => {
			const model = store().get('model');

			model.withMutations(map => {
				const jobs = map.get('jobs');

				jobs.forEach(j => {
					const removed = _.remove(j.siteVisitKeys, k => k === params.siteVisit.key);
					if (removed.length) {
						alteredJobs.push(j);
					}
				});
			});
		})
		.catch(err => {
			const model = store().get('model');

			model.withMutations(map => {
				_.forEach(alteredJobs, j => {
					const job = map.getIn(['jobs', j.key]);
					job.siteVisitKeys.push(params.siteVisit.key);
				});

				store().setIn(keyPath.slice(1), params.siteVisit);
			});
			globalActions.pushAlert({ type: 'warning', message: err.message || err, isCloseable: true });
		});
};

export const saveSiteVisit = params => {
	const keyPath = ['model', 'siteVisits', params.siteVisit.key];

	store().setIn(keyPath, siteVisitFactory(params.siteVisit));
	let jobKeyPath;

	// If there is a jobKey, then we need to add the jobcost key
	// to the job and update the store.
	if (params.siteVisit.jobKey) {
		jobKeyPath = ['model', 'jobs', params.siteVisit.jobKey];
		const job = store().getIn(jobKeyPath);

		if (job && job.siteVisitKeys.indexOf(params.siteVisit.key) === -1) {
			job.siteVisitKeys.push(params.siteVisit.key);

			store().setIn(jobKeyPath, jobModel(job));
		}
	}

	return serviceGateway.saveSiteVisit({ siteVisit: params.siteVisit, patch: params.siteVisit.dateCreated ? params.patch : undefined })
		.then(result => {
			store().setIn(keyPath, siteVisitFactory(result.siteVisit));
		})
		.catch(err => {
			const key = util.generateGuid();
			const retry = p => {
				globalActions.clearAlert({ key: key });
				return saveSiteVisit(p || params);
			};

			if (jobKeyPath) {
				const job = store().getIn(jobKeyPath);

				if (job) {
					_.remove(job.siteVisitKeys, f => {
						return f === params.siteVisit.key;
					});

					store().setIn(jobKeyPath, jobModel(job));
				}
			}

			globalActions.pushAlert({
				key: key,
				type: 'error',
				message: err.message || 'Server Error',
				source: 'server',
				action: 'saveSiteVisit',
				retry: retry,
				params: params,
				orgininalError: err,
				isCloseable: true
			});
		});
};